import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { buildQuery } from '@work4all/data/lib/hooks/data-provider/utils/buildQuery';

import { Country } from '@work4all/models/lib/Classes/Country.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';

import { useFormUpdate } from '../../../utils/use-form-update';
import { TravelExpensesFormValue } from '../type';

export const useTravelExpensesFormUpdate = (
  form: UseFormReturn<TravelExpensesFormValue>
) => {
  const client = useApolloClient();

  const updateCountry = useCallback(
    async (shortName: string): Promise<void> => {
      const requestData: DataRequest = {
        entity: Entities.country,
        data: {
          id: null,
          longtext: null,
          shortName: null,
        },
        filter: [{ shortName: { $eq: shortName } }],
      };

      const { query, variables, queryName } = buildQuery(requestData, 1);

      const result = await client.query({
        query,
        variables,
        fetchPolicy: 'cache-first',
      });

      const countries = result.data?.[queryName]?.data || [];
      const country = countries[0] as Country;
      form.setValue('country', country);
    },
    [client, form]
  );

  useFormUpdate(
    {
      // Recalculate totals when individual cost components change
      businessPartner: (businessPartner) => {
        const businessPartnerId = businessPartner?.id ?? 0;
        return {
          businessPartnerId,
          businessPartnerType:
            businessPartner?.businessPartnerType ?? SdObjType.LIEFERANT,
        };
      },
      visitedContact: (visitedContact) => {
        const visitedbusinessPartnerSDObjMemberId =
          visitedContact?.data?.id ?? 0;

        if (visitedContact?.data?.country)
          updateCountry(visitedContact?.data?.country ?? '');
        return {
          visitedbusinessPartnerSDObjMemberId,
          visitedbusinessPartnerSDObjType:
            'entity' in visitedContact &&
            visitedContact.entity === Entities.customer
              ? SdObjType.KUNDE
              : SdObjType.LIEFERANT,
          city: visitedContact?.data?.city ?? null,
        };
      },
      project: (project) => {
        const projectId = project?.id ?? 0;
        return {
          projectId,
        };
      },
      costCenter: (costCenter) => {
        const costCenterId = costCenter?.id ?? 0;
        return {
          costCenterId,
        };
      },
      country: (country) => {
        const countryId = country?.id ?? 0;
        return {
          countryId,
        };
      },
    },
    form
  );
};
